import {
  HiOutlineDeviceMobile,
  HiOutlineMinusCircle,
  HiOutlineRefresh,
} from 'react-icons/hi';
import { Spacer, useColorPreview } from './ColorPreview';
import './DesignSystem.css';
import {
  FaCodeBranch,
  FaLock,
  FaMap,
  FaMoon,
  FaPlus,
  FaUnlock,
  FaWindowMaximize,
} from 'react-icons/fa6';
import { useRef, useState } from 'react';
import { PiWarningThin } from 'react-icons/pi';
export type FontKind =
  | 'font-body'
  | 'font-caption'
  | 'font-palette-header'
  | 'font-subtitle'
  | 'font-entity-title'
  | 'font-source-code'
  | 'font-page-header'
  | 'font-preview-header'
  | 'font-preview-text'
  | 'font-landing-header'
  | 'font-slate-message';

export type FontProps = {
  kind: FontKind;
  style?: React.CSSProperties;
};
export function Font({
  kind,
  style,
  children,
}: React.PropsWithChildren<FontProps>) {
  return (
    <div style={{ display: 'contents', ...style }} className={kind}>
      {children}
    </div>
  );
}

export function HoverElementBoundary({ children }: React.PropsWithChildren) {
  return (
    <div className="hover-element-boundary" style={{ display: 'contents' }}>
      {children}
    </div>
  );
}

export function HoverElement({ children }: React.PropsWithChildren) {
  return (
    <div className="hover-element" style={{ display: 'contents' }}>
      {children}
    </div>
  );
}

export type InputProps = {
  value: string;
  onChange: (_: string) => void;
  type: 'email' | 'text' | 'password';
  placeholder: string;
  onBlur?: () => void;
  isDisabled?: boolean;
  fontKind?: FontKind;
};
export function Input({
  value,
  onChange,
  type,
  placeholder,
  onBlur,
  isDisabled,
  fontKind,
}: InputProps) {
  return (
    <Font kind={fontKind ?? 'font-body'}>
      <input
        className="input"
        style={{
          marginInline: 8,
          marginBlockEnd: 8,
          ...ThinBorderStyle,
          paddingInline: 8,
          paddingBlock: 4,
        }}
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        onBlur={() => onBlur?.()}
        disabled={isDisabled}
      />
    </Font>
  );
}

export type ActionType = 'remove' | 'locked' | 'unlocked';
export function ActionButton({
  action,
  hidden,
  color,
  onClick,
}: {
  action: ActionType;
  hidden: boolean;
  color?: string;
  onClick: () => void;
}) {
  if (hidden) {
    return <></>;
  }

  function iconFromAction(): IconProps['type'] {
    switch (action) {
      case 'remove':
        return 'remove';
      case 'locked':
        return 'locked';
      case 'unlocked':
        return 'unlocked';
    }
  }

  return (
    <HoverElement>
      <div
        style={{
          alignSelf: 'center',
          marginInline: 16,
        }}
      >
        <IconButton
          type={iconFromAction()}
          color={color}
          style={{
            visibility: action === 'locked' ? 'visible' : 'inherit',
          }}
          onClick={onClick}
        />
      </div>
    </HoverElement>
  );
}

export type PaletteInputProps = {
  value: string;
  onChange: (_: string) => void;
  type: 'text' | 'number';
  placeholder: string;
  onBlur?: () => void;
  disabled?: boolean;
  className?: string;
  fillWidth?: boolean;
  style?: React.CSSProperties;
  fontKind?: FontKind;
};
export function PaletteInput({
  value,
  onChange,
  type,
  placeholder,
  onBlur,
  disabled,
  className,
  fillWidth,
  style,
  fontKind,
}: PaletteInputProps) {
  return (
    <Font kind={fontKind ?? 'font-body'}>
      <input
        className={'palette-input ' + (className ?? '')}
        style={{
          paddingInline: 8,
          paddingBlock: 4,
          width: fillWidth ? '100%' : 'auto',
          ...style,
        }}
        type={type}
        placeholder={placeholder}
        disabled={disabled}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        onBlur={() => onBlur?.()}
      />
    </Font>
  );
}

export function SuccessMessage({ message }: { message: string }) {
  return (
    <Font
      kind="font-body"
      style={{ color: '#174dac', fontSize: 12, lineHeight: 1.4 }}
    >
      {message}
    </Font>
  );
}

export function ErrorMessage({ message }: { message: string }) {
  return (
    <Font
      kind="font-body"
      style={{ color: '#d93025', fontSize: 12, lineHeight: 1.4 }}
    >
      {message}
    </Font>
  );
}

export function ToggleSwitch({
  isOn,
  onChange,
}: {
  isOn?: boolean;
  onChange: (_: boolean) => void;
}) {
  const on = isOn === true;
  const backgroundColor = on ? '#00A0ED' : '#3C3C434C';
  return (
    <Row
      className="toggle-switch"
      onClick={() => {
        onChange(!(isOn === true));
      }}
      style={{
        alignItems: 'center',
        justifyContent: isOn === true ? 'flex-end' : 'flex-start',
        backgroundColor,
      }}
    >
      <input
        type="checkbox"
        checked={isOn}
        onChange={(e) => {
          onChange(e.target.checked);
        }}
      />
      <div className="toggle-switch-switch" />
    </Row>
  );
}

export function ColorIndicator({ option }: { option: string }) {
  const { resolveColor } = useColorPreview();
  const textColor = resolveColor({ type: 'reference', reference: option });
  return (
    <div
      style={{
        width: 12,
        height: 12,
        backgroundColor: textColor,
        marginInlineEnd: 8,
        borderRadius: 4,
        border: '1px solid black',
      }}
    />
  );
}
export function TextColorOption({ option }: { option: string }) {
  return (
    <Row style={{ width: '100%', alignItems: 'center' }}>
      <ColorIndicator option={option} />
      <div>{option}</div>
    </Row>
  );
}

export type TypeaheadInputProps = {
  value: string;
  onChange: (_: string) => void;
  onCommit?: (_: string) => void;
  options: string[];
  renderOption?: (_: { option: string }) => React.ReactNode;
  renderOptionIndicator?: (_: { option: string }) => React.ReactNode;
  placeholder: string;
  style?: React.CSSProperties;
  fontKind?: FontKind;
};
export function TypeaheadInput({
  value,
  onChange,
  onCommit,
  placeholder,
  options,
  renderOption,
  renderOptionIndicator,
  style,
  fontKind,
}: TypeaheadInputProps) {
  const [showTypeahead, setShowTypeahead] = useState(false);
  function Option({ option }: { option: string }) {
    if (renderOption !== undefined) {
      return <div>{renderOption({ option })}</div>;
    }

    return <div>{option}</div>;
  }

  const borderStyle: React.CSSProperties = {
    borderColor: showTypeahead ? '#00a2ed' : 'black',
    borderWidth: showTypeahead ? '2px' : '1px',
    borderStyle: 'solid',
  };

  return (
    <Font kind={fontKind ?? 'font-body'}>
      <Column
        style={{
          ...style,
        }}
      >
        <Row
          className="palette-input"
          style={{
            alignItems: 'center',
            padding: 4,
            marginBlockEnd: 8,
            ...borderStyle,
            backgroundColor: 'white',
            width: 'auto',
          }}
        >
          {renderOptionIndicator?.({ option: value })}
          <input
            className={fontKind ?? 'font-body'}
            style={{ width: 'auto', border: 'none', outline: 'none' }}
            type="text"
            placeholder={placeholder}
            value={value}
            onFocus={() => setShowTypeahead(true)}
            onClick={() => setShowTypeahead(true)}
            onBlur={() => {
              setShowTypeahead(false);
              onCommit?.(value);
            }}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') {
                return;
              }

              e.preventDefault();
              setShowTypeahead(false);
              onCommit?.(value);
              e.currentTarget.blur();
            }}
            onChange={(e) => {
              onChange(e.target.value);
            }}
          />
        </Row>
        <Column>
          {showTypeahead &&
            options.map((option) => {
              return (
                <div
                  key={option}
                  onMouseDown={(e) => e.preventDefault()}
                  style={{ padding: 4, paddingBlock: 12 }}
                  onClick={() => {
                    setShowTypeahead(false);
                    onChange(option);
                    onCommit?.(option);
                  }}
                >
                  <Option option={option} />
                </div>
              );
            })}
        </Column>
      </Column>
    </Font>
  );
}

export type CharacterInputProps = {
  value: string;
  onChange: (_: string) => void;
  isLoading?: boolean;
  inputRef?: React.Ref<HTMLInputElement>;
  ariaLabel?: string;
};

const CharacterInputWidth = 48;

export function CharacterInput({
  value,
  onChange,
  isLoading,
  inputRef,
  ariaLabel,
}: CharacterInputProps) {
  const fallbackRef = useRef<HTMLInputElement>(null);
  const fillAccentColor = '#004BB399';
  const passAccentColor = '#004BB300';
  const cssVariableSubstitutions = {
    '--loader-background-a': fillAccentColor,
    '--loader-background-b': passAccentColor,
  } as React.CSSProperties;

  return (
    <Font kind="font-entity-title">
      <input
        ref={inputRef ?? fallbackRef}
        className={'input' + (isLoading === true ? ' vertical-loader' : '')}
        style={{
          ...cssVariableSubstitutions,
          marginInline: 8,
          marginBlockEnd: 8,
          ...ThinBorderStyle,
          paddingInline: 8,
          paddingBlock: 4,
          width: `${CharacterInputWidth}px`,
          height: `${CharacterInputWidth * 1.5}px`,
          textAlign: 'center',
        }}
        type="text"
        aria-label={ariaLabel}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          e.preventDefault();
        }}
        disabled={isLoading}
      />
    </Font>
  );
}

export type PrimaryButtonProps = {
  text: string;
  isLoading?: boolean;
  isDisabled?: boolean;
  style?: React.CSSProperties;
  onClick: () => void;
};

export type PrimaryLinkButtonProps = {
  text: string;
  href: string;
  style?: React.CSSProperties;
  target?: React.HTMLAttributeAnchorTarget;
  rel?: string;
};
export function PrimaryButton({
  text,
  isLoading,
  isDisabled,
  style,
  onClick,
}: PrimaryButtonProps) {
  return (
    <Font kind="font-caption">
      <button
        className="primary-button"
        style={style}
        disabled={isDisabled || isLoading}
        onClick={() => onClick()}
      >
        <Row style={{ height: '100%', alignItems: 'center' }}>
          {isLoading && (
            <div style={{ position: 'absolute', marginInlineStart: 8 }}>
              <LoadingSpinner />
            </div>
          )}
          <Spacer />
          <div>{text}</div>
          <Spacer />
        </Row>
      </button>
    </Font>
  );
}

export function PrimaryLinkButton({
  text,
  href,
  style,
  target,
  rel,
}: PrimaryLinkButtonProps) {
  return (
    <Font kind="font-caption">
      <a
        className="primary-button primary-link-button"
        href={href}
        style={style}
        target={target}
        rel={rel}
      >
        <Row style={{ height: '100%', alignItems: 'center' }}>
          <Spacer />
          <div>{text}</div>
          <Spacer />
        </Row>
      </a>
    </Font>
  );
}

export type SecondaryButtonProps = PrimaryButtonProps;
export function SecondaryButton({
  text,
  onClick,
  isDisabled,
}: SecondaryButtonProps) {
  return (
    <Font kind="font-caption">
      <button
        type="button"
        className="secondary-button"
        onClick={() => onClick()}
        disabled={isDisabled}
      >
        {text}
      </button>
    </Font>
  );
}

export type IconProps = {
  type:
    | 'remove'
    | 'locked'
    | 'unlocked'
    | 'regenerate'
    | 'dark-mode'
    | 'warning'
    | 'map'
    | 'plus'
    | 'window-maximize'
    | 'versions'
    | 'device-selector';
  color?: string;
};

export function Icon({ type, color }: IconProps) {
  return (
    <>
      {type === 'remove' && (
        <HiOutlineMinusCircle size={IconButtonIconSize} color={color} />
      )}
      {type === 'locked' && <FaLock size={IconButtonIconSize} color={color} />}
      {type === 'unlocked' && (
        <FaUnlock size={IconButtonIconSize} color={color} />
      )}
      {type === 'regenerate' && (
        <HiOutlineRefresh size={IconButtonIconSize} color={color} />
      )}
      {type === 'dark-mode' && (
        <FaMoon size={IconButtonIconSize} color={color} />
      )}
      {type === 'warning' && (
        <PiWarningThin size={IconButtonIconSize} color={color} />
      )}
      {type === 'map' && <FaMap size={IconButtonIconSize} color={color} />}
      {type === 'plus' && <FaPlus size={IconButtonIconSize} color={color} />}
      {type === 'window-maximize' && (
        <FaWindowMaximize size={IconButtonIconSize} color={color} />
      )}
      {type === 'versions' && (
        <FaCodeBranch size={IconButtonIconSize} color={color} />
      )}
      {type === 'device-selector' && (
        <HiOutlineDeviceMobile size={IconButtonIconSize} color={color} />
      )}
    </>
  );
}

export type IconButtonProps = {
  text?: string;
  onClick: () => void;
  className?: string;
  style?: React.CSSProperties;
} & IconProps;

export const IconButtonIconSize = 20;
export function IconButton({
  type,
  text,
  onClick,
  color,
  className,
  style,
}: IconButtonProps) {
  return (
    <Row
      onClick={onClick}
      style={{ color: color ?? 'inherit', ...style }}
      className={'icon-button ' + (className ?? '')}
    >
      <Icon type={type} color={color} />
      {text && (
        <Font kind="font-caption">
          <div style={{ marginInlineStart: 4 }}>{text}</div>
        </Font>
      )}
    </Row>
  );
}

export type ScrollableProps = {
  style?: React.CSSProperties;
};
export function Scrollable({
  style,
  children,
}: React.PropsWithChildren<ScrollableProps>) {
  return (
    <div className="scrollable" style={style}>
      {children}
    </div>
  );
}

export function NavigationFilter<T extends string>({
  options,
  onSelection,
  warningOptions,
}: {
  options: T[];
  onSelection: (_: T) => void;
  warningOptions?: T[];
}) {
  const [selectedIndex, setSelectedIndex] = useState<number>(0);

  return (
    <Row style={{ alignItems: 'center' }}>
      {options.map((option, i) => {
        const isSelected = selectedIndex === i;
        return (
          <Row
            key={option}
            onClick={() => {
              onSelection(option);
              setSelectedIndex(i);
            }}
            style={{
              color: isSelected ? 'white' : PrimaryActionColor,
              backgroundColor: isSelected ? PrimaryActionColor : '',
              height: '24px',
              paddingInline: '8px',
              marginInline: '8px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {warningOptions?.includes(option) && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginInlineEnd: '4px',
                }}
              >
                <Icon type="warning" />
              </div>
            )}
            <Font
              kind="font-caption"
              style={{ fontStyle: isSelected ? 'bold' : 'normal' }}
            >
              <div>{option}</div>
            </Font>
          </Row>
        );
      })}
    </Row>
  );
}

function LoadingSpinner() {
  return <div className="loading-spinner" />;
}

export type TransparentDivProps = {
  style?: React.CSSProperties;
  className?: string;
  onClick?: () => void;
} & Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'style' | 'className' | 'onClick'
>;

export function Row({
  style,
  className,
  onClick,
  children,
  ...rest
}: React.PropsWithChildren<TransparentDivProps>) {
  return (
    <div
      {...rest}
      className={className}
      style={{ display: 'flex', flexDirection: 'row', ...style }}
      onClick={onClick}
    >
      {children}
    </div>
  );
}

export function Column({
  style,
  className,
  onClick,
  children,
  ...rest
}: React.PropsWithChildren<TransparentDivProps>) {
  return (
    <div
      {...rest}
      className={className}
      style={{ display: 'flex', flexDirection: 'column', ...style }}
      onClick={onClick}
    >
      {children}
    </div>
  );
}

export const PrimaryActionColor = '#00A0ED';
export const ElevatedBorderColor = '#ffffff';
// The `ElevatedBorderFlatWidth` is the border width of the edges that do not appear elevated above the content
export const ElevatedBorderFlatWidth = '1px';

export const ThinBorderStyle: React.CSSProperties = {
  border: `1px solid ${ElevatedBorderColor}`,
  borderRadius: 8,
};

export const LockedBorderStyle: React.CSSProperties = {
  border: `2px solid ${ElevatedBorderColor}`,
  borderRadius: 8,
};

export const FlatBorderStyle: React.CSSProperties = {
  border: `${ElevatedBorderFlatWidth} solid ${ElevatedBorderColor}`,
  borderRadius: 8,
};
export const ElevatedBorderStyle: React.CSSProperties = {
  borderStyle: 'solid',
  borderTopWidth: ElevatedBorderFlatWidth,
  borderLeftWidth: ElevatedBorderFlatWidth,
  borderBottomWidth: '3px',
  borderRightWidth: '3px',
  borderColor: ElevatedBorderColor,
};

export const PaletteBorderStyle: React.CSSProperties = {
  ...ElevatedBorderStyle,
  borderTopLeftRadius: 4,
  borderTopRightRadius: 20,
  borderBottomRightRadius: 20,
  borderBottomLeftRadius: 20,
};
